import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, RefreshCw, ClipboardCopy, ExternalLink, Megaphone, Tag, FolderTree, Type, Save } from "lucide-react";

const METADATA_COST = 3;
const KDP_EBOOK = "https://kdp.amazon.com/en_US/title-setup/kindle/new/details";
const KDP_PAPERBACK = "https://kdp.amazon.com/en_US/title-setup/paperback/new/details";

function CopyBtn({ text, label = "Copy", testid }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Couldn't copy — select and copy manually"); }
  };
  return (
    <Button type="button" variant="ghost" size="sm" onClick={copy} data-testid={testid} className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground">
      <ClipboardCopy className="w-3.5 h-3.5" /> {label}
    </Button>
  );
}

const boxCls = "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm leading-relaxed";

function Field({ icon: Icon, label, copy, copyLabel, testid, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-primary flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</span>
        <CopyBtn text={copy} label={copyLabel} testid={testid} />
      </div>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

const toDraft = (m) => ({
  blurb: m?.blurb || "", subtitle: m?.subtitle || "",
  keywords: Array.from({ length: 7 }, (_, i) => (m?.keywords || [])[i] || ""),
  categories: (m?.categories || []).join("\n"),
});

export default function MetadataDialog({ open, onOpenChange, project }) {
  const [draft, setDraft] = useState(toDraft(null));
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!project?.id) return;
    setLoading(true);
    api.get(`/projects/${project.id}/metadata`)
      .then(({ data }) => setMeta(data && (data.blurb || data.subtitle || (data.keywords || []).length) ? data : null))
      .catch(() => setMeta(null))
      .finally(() => setLoading(false));
  }, [project?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { setDraft(toDraft(meta)); }, [meta]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setKeyword = (i, v) => setDraft((d) => ({ ...d, keywords: d.keywords.map((x, j) => (j === i ? v : x)) }));
  const dirty = !!meta && JSON.stringify(draft) !== JSON.stringify(toDraft(meta));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${project.id}/metadata`, {
        blurb: draft.blurb, subtitle: draft.subtitle, keywords: draft.keywords,
        categories: draft.categories.split("\n"),
      });
      setMeta(data);
      toast.success("Listing details saved — the KDP kit will use them");
    } catch { toast.error("Couldn't save your changes"); }
    finally { setSaving(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      if (dirty && !window.confirm("Regenerating will replace your unsaved edits. Continue?")) { setBusy(false); return; }
      const { data } = await api.post(`/projects/${project.id}/metadata/generate`);
      setMeta(data);
      toast.success("Metadata pack ready");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "out_of_credits") toast.error(`Not enough credits — a metadata pack costs ${METADATA_COST} (you have ${d.balance}).`);
      else toast.error(typeof d === "string" ? d : "Couldn't generate the metadata pack — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="bg-popover max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="metadata-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> KDP metadata pack
          </DialogTitle>
          <DialogDescription>
            AI-written blurb, subtitle, 7 Amazon keywords and category picks. Edit anything, then save — the KDP kit uses your saved version. Costs {METADATA_COST} credits to (re)generate.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : !meta ? (
          <div className="py-8 text-center space-y-4" data-testid="metadata-empty">
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              We’ll craft your book’s marketing copy from its title, genre, synopsis and opening pages.
            </p>
            <Button onClick={generate} disabled={busy} data-testid="metadata-generate" className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate metadata pack · {METADATA_COST} credits
            </Button>
          </div>
        ) : (
          <div className="space-y-5 py-1" data-testid="metadata-content">
            <Field icon={Megaphone} label="Book description (blurb)" copy={draft.blurb} testid="copy-blurb"
              hint={`${draft.blurb.length.toLocaleString()} / 4,000 characters`}>
              <textarea value={draft.blurb} onChange={(e) => set("blurb", e.target.value.slice(0, 4000))} rows={8}
                className={boxCls} data-testid="metadata-blurb" />
            </Field>
            <Field icon={Type} label="Subtitle" copy={draft.subtitle} testid="copy-subtitle">
              <input value={draft.subtitle} onChange={(e) => set("subtitle", e.target.value.slice(0, 200))}
                className={boxCls} placeholder="Optional" data-testid="metadata-subtitle" />
            </Field>
            <Field icon={Tag} label="7 keywords" copy={draft.keywords.filter(Boolean).join(", ")} copyLabel="Copy all" testid="copy-keywords"
              hint="One keyword or phrase per box (Amazon allows up to 7).">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="metadata-keywords">
                {draft.keywords.map((k, i) => (
                  <input key={i} value={k} onChange={(e) => setKeyword(i, e.target.value.slice(0, 50))}
                    className={boxCls} placeholder={`Keyword ${i + 1}`} data-testid={`metadata-keyword-${i}`} />
                ))}
              </div>
            </Field>
            <Field icon={FolderTree} label="Category picks" copy={draft.categories} copyLabel="Copy all" testid="copy-categories"
              hint="One category per line.">
              <textarea value={draft.categories} onChange={(e) => set("categories", e.target.value)} rows={4}
                className={boxCls} data-testid="metadata-categories" />
            </Field>
            <div className="flex items-center justify-end gap-2">
              {dirty && <span className="text-[11px] text-amber-500" data-testid="metadata-unsaved">Unsaved changes</span>}
              <Button size="sm" onClick={save} disabled={!dirty || saving} className="gap-1.5" data-testid="metadata-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
              <Button type="button" variant="outline" size="sm" onClick={generate} disabled={busy} data-testid="metadata-regenerate" className="gap-1.5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate
              </Button>
              <div className="flex-1" />
              <Button asChild size="sm" data-testid="kdp-create-ebook" className="gap-1.5">
                <a href={KDP_EBOOK} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /> Create eBook on KDP</a>
              </Button>
              <Button asChild variant="outline" size="sm" data-testid="kdp-create-paperback" className="gap-1.5">
                <a href={KDP_PAPERBACK} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /> Paperback</a>
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">KDP opens in a new tab (you’ll sign in there). Amazon caps descriptions at 4,000 characters and allows up to 7 keywords — paste each field into the matching box.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
